import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
interface PrivacySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacySettingsModal({
  isOpen,
  onClose
}: PrivacySettingsModalProps) {
  const {
    user,
    refreshUser
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    dataSharing: false,
    profileVisibility: 'private' as 'public' | 'private',
    analyticsTracking: true,
    marketingEmails: false,
    scanHistoryRetention: '90' as '30' | '90' | '180' | 'forever'
  });
  useEffect(() => {
    if (isOpen && user) {
      loadPrivacySettings();
    }
  }, [isOpen, user]);
  const loadPrivacySettings = () => {
    const privacySettings = user?.user_metadata?.privacySettings || {};
    setSettings({
      dataSharing: privacySettings.dataSharing || false,
      profileVisibility: privacySettings.profileVisibility || 'private',
      analyticsTracking: privacySettings.analyticsTracking ?? true,
      marketingEmails: privacySettings.marketingEmails || false,
      scanHistoryRetention: privacySettings.scanHistoryRetention || '90'
    });
  };
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        error: updateError
      } = await supabase.auth.updateUser({
        data: {
          ...user?.user_metadata,
          privacySettings: settings
        }
      });
      if (updateError) throw updateError;
      await refreshUser();
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2003" data-mp="2vesg4d9dp47y">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" data-id="element-2004" data-mp="2qg7c02r0a0e8">
        <div className="flex items-center justify-between mb-6" data-id="element-2005" data-mp="2loz79k0mf37e">
          <h2 className="text-xl font-bold" data-id="element-2006" data-mp="2ln5wptmvzyu4">Privacy Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-2007" data-mp="2gp4hjjsl0jnx">
            <X size={20} data-id="element-2008" data-mp="2bsmcsjx94lbl" />
          </button>
        </div>
        {success ? <div className="text-center py-8" data-id="element-2009" data-mp="27oep8q2j5bey">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2010" data-mp="27ru1ll4tk6co">
              <Check size={32} className="text-green-600" data-id="element-2011" data-mp="1xra0qjl7p3uz" />
            </div>
            <h3 className="text-lg font-semibold mb-2" data-id="element-2012" data-mp="1xtedzs503f7x">Settings Saved!</h3>
            <p className="text-gray-600 text-sm" data-id="element-2013" data-mp="yhvnsul3dmu">
              Your privacy preferences have been updated
            </p>
          </div> : <div className="space-y-6" data-id="element-2014" data-mp="3jpkewq0n4yb">
            {/* Data Sharing */}
            <div className="border-b border-gray-200 pb-4" data-id="element-2015" data-mp="3hd4sq7qd6of">
              <div className="flex items-center justify-between mb-2" data-id="element-2016" data-mp="8h7yt2msycdp">
                <div data-id="element-2017" data-mp="d8pouvnaesjw">
                  <h3 className="font-medium text-sm" data-id="element-2018" data-mp="daalcqt5wlke">Data Sharing</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2019" data-mp="i8vjmkousv67">
                    Allow anonymous usage data collection for product
                    improvement
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-2020" data-mp="3loj4l89snzzw">
                  <input type="checkbox" checked={settings.dataSharing} onChange={e => setSettings({
                ...settings,
                dataSharing: e.target.checked
              })} className="sr-only peer" data-id="element-2021" data-mp="3qof7f46r9ldv" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-2022" data-mp="18icteo9od67r"></div>
                </label>
              </div>
            </div>
            {/* Profile Visibility */}
            <div className="border-b border-gray-200 pb-4" data-id="element-2023" data-mp="1ng57dbzkpmju">
              <h3 className="font-medium text-sm mb-3" data-id="element-2024" data-mp="1ndiy97x1ayzi">Profile Visibility</h3>
              <div className="space-y-2" data-id="element-2025" data-mp="216iejc4n4x84">
                <label className="flex items-center" data-id="element-2026" data-mp="1w7xfhg14kjl9">
                  <input type="radio" name="profileVisibility" checked={settings.profileVisibility === 'public'} onChange={() => setSettings({
                ...settings,
                profileVisibility: 'public'
              })} className="mr-2" data-id="element-2027" data-mp="1w8gtwmrkgpuj" />
                  <span className="text-sm" data-id="element-2028" data-mp="2ftpeynwi2csr">Public</span>
                </label>
                <label className="flex items-center" data-id="element-2029" data-mp="2fvtgx0au91qe">
                  <input type="radio" name="profileVisibility" checked={settings.profileVisibility === 'private'} onChange={() => setSettings({
                ...settings,
                profileVisibility: 'private'
              })} className="mr-2" data-id="element-2030" data-mp="2krqz7c5nwi5t" />
                  <span className="text-sm" data-id="element-2031" data-mp="2207y5av77e6f">Private</span>
                </label>
              </div>
            </div>
            {/* Analytics Tracking */}
            <div className="border-b border-gray-200 pb-4" data-id="element-2032" data-mp="1s337axqequja">
              <div className="flex items-center justify-between mb-2" data-id="element-2033" data-mp="18zj7yz9umw0g">
                <div data-id="element-2034" data-mp="190udb3iy0bto">
                  <h3 className="font-medium text-sm" data-id="element-2035" data-mp="148t4jrkzh38e">Analytics Tracking</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2036" data-mp="147hy1k05lhdl">
                    Help us improve by tracking your usage patterns
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-2037" data-mp="uby6tawjb6v6">
                  <input type="checkbox" checked={settings.analyticsTracking} onChange={e => setSettings({
                ...settings,
                analyticsTracking: e.target.checked
              })} className="sr-only peer" data-id="element-2038" data-mp="1rynd3wy9s5mu" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-2039" data-mp="6m3vixrv5qd1"></div>
                </label>
              </div>
            </div>
            {/* Marketing Emails */}
            <div className="border-b border-gray-200 pb-4" data-id="element-2040" data-mp="2ryz8d2oijc8s">
              <div className="flex items-center justify-between mb-2" data-id="element-2041" data-mp="2wxtry6ub14ty">
                <div data-id="element-2042" data-mp="2wzmzdocqrt30">
                  <h3 className="font-medium text-sm" data-id="element-2043" data-mp="30ym355mqljsv">Marketing Emails</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2044" data-mp="310qfmbypbr4r">
                    Receive promotional emails and product updates
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-2045" data-mp="3awjq7psv1ajw">
                  <input type="checkbox" checked={settings.marketingEmails} onChange={e => setSettings({
                ...settings,
                marketingEmails: e.target.checked
              })} className="sr-only peer" data-id="element-2046" data-mp="1bl951y57ws07" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-2047" data-mp="3s52qwwodynw0"></div>
                </label>
              </div>
            </div>
            {/* Scan History Retention */}
            <div className="pb-4" data-id="element-2048" data-mp="34fi8cew59sn9">
              <h3 className="font-medium text-sm mb-3" data-id="element-2049" data-mp="opf6qbb9rn3d">
                Scan History Retention
              </h3>
              <select value={settings.scanHistoryRetention} onChange={e => setSettings({
            ...settings,
            scanHistoryRetention: e.target.value as any
          })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent" data-id="element-2050" data-mp="jr3xa1ovzelo">
                <option value="30" data-id="element-2051" data-mp="md389ps0zrar">30 days</option>
                <option value="90" data-id="element-2052" data-mp="1a0bce4dpwub7">90 days</option>
                <option value="180" data-id="element-2053" data-mp="1a2o3b71gef0e">180 days</option>
                <option value="forever" data-id="element-2054" data-mp="1ezxtiowo2upa">Forever</option>
              </select>
              <p className="text-xs text-gray-500 mt-2" data-id="element-2055" data-mp="118j4a0c61x9s">
                How long to keep your scan history before automatic deletion
              </p>
            </div>
            {error && <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200" data-id="element-2056" data-mp="1szpc72y4oz9m">
                {error}
              </div>}
            <div className="flex gap-3 pt-2" data-id="element-2057" data-mp="1xrgsmknklom1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-2058" data-mp="2k7clhxa5eek8">
                Cancel
              </button>
              <button onClick={handleSave} disabled={loading} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2059" data-mp="2ab9gmjnww1tl">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}